import type { AiContextAttachmentType } from "@productlane/lib/src/ai"

import type { ContextMentionItem } from "./context-mention"
import {
	CONTEXT_MENTION_GROUP_LABELS,
	contextMentionKey,
} from "./context-mention"

export interface ContextMentionSection {
	key: string
	label: string
	items: ContextMentionItem[]
}

// Fixed order so the list never reshuffles under the cursor as results land
// from separate queries.
export const CONTEXT_MENTION_TYPE_ORDER: AiContextAttachmentType[] = [
	"thread",
	"issue",
	"article",
	"project",
	"changelog",
	"user",
	"contact",
	"company",
]

// With nothing typed the picker stays short and offers the three things a
// copilot turn is usually about; searching reaches every group.
export const CONTEXT_MENTION_DEFAULT_TYPES: AiContextAttachmentType[] = [
	"thread",
	"issue",
	"article",
]

export const CONTEXT_MENTION_PAGE_SECTION_KEY = "page"

const normalize = (value: string) => value.trim().toLowerCase()

// Lower is better. -1 drops the row.
export const scoreContextMention = (
	item: ContextMentionItem,
	query: string,
): number => {
	const q = normalize(query)
	if (!q) return 0
	const haystacks = [item.prefix, item.label]
		.filter((value): value is string => Boolean(value))
		.map(normalize)
	let best = -1
	for (const hay of haystacks) {
		const at = hay.indexOf(q)
		if (at < 0) continue
		const score = at === 0 ? 0 : /\s/.test(hay[at - 1] ?? "") ? 1 : 2
		if (best < 0 || score < best) best = score
	}
	return best
}

export const rankContextMentions = (
	items: ContextMentionItem[],
	query: string,
	limit: number,
): ContextMentionItem[] =>
	items
		.map((item, index) => ({
			item,
			index,
			score: scoreContextMention(item, query),
		}))
		.filter((entry) => entry.score >= 0)
		.sort((a, b) => a.score - b.score || a.index - b.index)
		.slice(0, limit)
		.map((entry) => entry.item)

// The record the user is looking at leads the list, the way it does in Linear,
// and never repeats itself further down.
export const buildContextMentionSections = (
	itemsByType: Partial<Record<AiContextAttachmentType, ContextMentionItem[]>>,
	query: string,
	options?: {
		perType?: number
		total?: number
		pageItem?: ContextMentionItem | null
	},
): ContextMentionSection[] => {
	const perType = options?.perType ?? 3
	const total = options?.total ?? 15
	const pageItem = options?.pageItem ?? null
	const sections: ContextMentionSection[] = []
	let used = 0

	if (pageItem && scoreContextMention(pageItem, query) >= 0) {
		sections.push({
			key: CONTEXT_MENTION_PAGE_SECTION_KEY,
			label: "This page",
			items: [pageItem],
		})
		used += 1
	}

	const pageKey = pageItem ? contextMentionKey(pageItem) : null

	const types = query.trim()
		? CONTEXT_MENTION_TYPE_ORDER
		: CONTEXT_MENTION_DEFAULT_TYPES

	for (const type of types) {
		if (used >= total) break
		const pool = (itemsByType[type] ?? []).filter(
			(item) => contextMentionKey(item) !== pageKey,
		)
		const ranked = rankContextMentions(
			pool,
			query,
			Math.min(perType, total - used),
		)
		if (ranked.length === 0) continue
		used += ranked.length
		sections.push({
			key: type,
			label: CONTEXT_MENTION_GROUP_LABELS[type],
			items: ranked,
		})
	}

	return sections
}

export const flattenContextMentionSections = (
	sections: ContextMentionSection[],
): ContextMentionItem[] => sections.flatMap((section) => section.items)
